import { Button } from "@tellescope/design-system";
import {
  FilterBlockEditor,
  Label,
  MultiSelect,
  type MultiSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from "@tellescope/ui";
import { type FreeformFieldOption, type FreeformFilterBlock } from "@tellescope/utils";
import { ListFilter } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface TimelineFilterPopoverProps {
  filterBlock: FreeformFilterBlock;
  fieldOptions: FreeformFieldOption[];
  onFilterChange: (block: FreeformFilterBlock) => void;
  combineSMS: boolean;
  onCombineSMSChange: (next: boolean) => void;
}

export const TimelineFilterPopover = ({
  filterBlock,
  fieldOptions,
  onFilterChange,
  combineSMS,
  onCombineSMSChange,
}: TimelineFilterPopoverProps) => {
  const { t } = useTranslation("contacts");

  const typeOptions = useMemo<MultiSelectOption[]>(
    () => [
      { value: "phone_call", label: t("Phone Calls") },
      { value: "sms_message", label: t("SMS Messages") },
      { value: "chat_room", label: t("Chat Rooms") },
      { value: "email", label: t("Emails") },
      { value: "form_response_group", label: t("Form Responses") },
      { value: "note", label: t("Notes") },
    ],
    [t]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button iconOnly variant="ghost" size="sm" color="muted" hoverLabel={t("Filter timeline")}>
          <ListFilter />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="end">
        <FilterBlockEditor
          block={filterBlock}
          fieldOptions={fieldOptions}
          onChange={onFilterChange}
          onRemove={() => onFilterChange({ ...filterBlock, filters: [] })}
          renderValueInput={({ filter, onChange }) => (
            <MultiSelect
              className="w-full bg-white text-xs font-light"
              options={typeOptions}
              value={Array.isArray(filter.value) ? filter.value : []}
              onValueChange={(value) => onChange({ ...filter, value })}
              placeholder={t("Select types")}
            />
          )}
        />
        <div className="flex items-center justify-between gap-2 border-t px-1 pt-2">
          <Label htmlFor="timeline-combine-sms" className="font-normal">
            {t("Combine SMS messages")}
          </Label>
          <Switch id="timeline-combine-sms" checked={combineSMS} onCheckedChange={onCombineSMSChange} />
        </div>
      </PopoverContent>
    </Popover>
  );
};
